import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import FlexGrid from "@tds/core-flex-grid";
import HairlineDivider from "@tds/core-hairline-divider";
import Box from "@tds/core-box";
import Text from "@tds/core-text";
import Responsive from "@tds/core-responsive";
import Link from "@tds/core-link";
import TextButton from "@tds/core-text-button";
import EppLogo from "../images/logo.png";
import SbsEnLogo from "../images/logo_sbs_en_2022.png";
import SbsFrLogo from "../images/logo_sbs_fr_2022.png";
import "../../../css/dist/header.css";

function Header({
  title,
  pageLogo,
  pageLogoAlt,
  stream,
  currentLanguage,
  changeLanguageCallback,
  telusSiteText,
  tracking,
  callbackLink,
  t,
}) {
  const [headerLogo, setHeaderLogo] = useState(EppLogo);

  useEffect(() => {
    if (stream === "sbs") {
      setHeaderLogo(currentLanguage === "en" ? SbsEnLogo : SbsFrLogo);
    }

    if (stream !== "sbs" && pageLogo !== "") {
      setHeaderLogo(pageLogo);
    }
  }, []);

  // Site exit can have tracking for EPP.
  // Trailing slash required for correct language parsing when there are URL
  // parameters on target site.
  const telusSite = new URL(`https://www.telus.com/${currentLanguage}/`);
  if (stream === "epp" && tracking) {
    telusSite.searchParams.append("INTCMP", tracking);
  }

  let languagePicker;
  const languages = [
    {
      name: t("english"),
      langCode: "en",
    },
    {
      name: t("french"),
      langCode: "fr",
    },
  ];

  languages.forEach((language) => {
    if (language.langCode !== currentLanguage) {
      languagePicker = (
        <div className="language-switcher">
          <>
            <TextButton
              onClick={(e) => changeLanguageCallback(e, language.langCode)}
              style={{ padding: 0 }}
            >
              <Responsive
                maxWidth="md"
                render={() => <Text>{language.langCode.toUpperCase()}</Text>}
              />
              <Responsive
                minWidth="md"
                render={() => <Text>{language.name}</Text>}
              />
            </TextButton>
          </>
        </div>
      );
    }
  });

  return (
    <div className="header">
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col>
            <Box inline vertical={3} between={{ lg: 3 }}>
              <img
                style={{
                  maxHeight: "28px",
                  maxWidth: "152px",
                }}
                alt={pageLogoAlt}
                src={headerLogo}
              />
              <Responsive
                minWidth="lg"
                render={() => (
                  <>
                    {title !== "" && <HairlineDivider vertical />}
                    <span>{title}</span>
                  </>
                )}
              />
            </Box>
          </FlexGrid.Col>
          <FlexGrid.Col horizontalAlign="right">
            <Box className="header-links" inline vertical={3} between={3}>
              {callbackLink && (
                <Link href={callbackLink}>{t("requestCallback.title")}</Link>
              )}
              {languagePicker}
              <HairlineDivider vertical />
              <Link href={telusSite.toString()}>
                <Responsive
                  maxWidth="md"
                  render={() => <Text>{t("headerShortTelusSiteText")}</Text>}
                />
                <Responsive
                  minWidth="md"
                  render={() => <Text>{telusSiteText}</Text>}
                />
              </Link>
            </Box>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
      <HairlineDivider />
    </div>
  );
}

Header.propTypes = {
  // `epp` or `sbs`
  stream: PropTypes.string,
  title: PropTypes.string,
  pageLogo: PropTypes.string,
  pageLogoAlt: PropTypes.string,
  currentLanguage: PropTypes.string,
  telusSiteText: PropTypes.string.isRequired,
  callbackLink: PropTypes.string,
  changeLanguageCallback: PropTypes.func.isRequired,
  tracking: PropTypes.string,
  t: PropTypes.func.isRequired,
};

Header.defaultProps = {
  stream: "epp",
  title: "",
  pageLogo: "",
  pageLogoAlt: "TELUS Logo",
  currentLanguage: "en",
  tracking: null,
  callbackLink: null,
};

export default withTranslation()(Header);
